import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPreferences } from "@coworker/types/lib/userProfileTypes";
import userPreferences from "@coworker/enums/profilePreferences";
import { useUID } from "../core/auth/useLoggedInUser";
import { httpClient, HttpError } from "@coworker/http-client";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import useFlag, { FLAGS } from "./useFlag";
import {
  mapCWHPreferencesForFrontend,
  toCwhUserPreferences,
  toUserPreferenceList,
} from "./API/cwhPreferences";

interface PreferencesResponse {
  preferences: UserPreferences;
}

const isChina = isChineseEnvironment();

const CWH_PREFERENCES_PATH = "/user-preferences";

async function fetchUserPreferences(uid: string): Promise<UserPreferences> {
  const data = await httpClient.core.get<PreferencesResponse>(
    `/users/${uid}/preferences`
  );
  return data?.preferences as UserPreferences;
}

/**
 * Fetches the raw CWH preferences payload directly from the CWH gateway. A brand
 * new user has no preferences and CWH answers with 404 — treated as empty.
 */
async function fetchRawCwhPreferences(): Promise<unknown> {
  try {
    return await httpClient.cwh.get<unknown>(CWH_PREFERENCES_PATH);
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return { userPreferences: [] };
    }
    throw error;
  }
}

async function fetchCwhUserPreferences(): Promise<UserPreferences> {
  const raw = await fetchRawCwhPreferences();
  return mapCWHPreferencesForFrontend(raw);
}

function useUserPreferences() {
  const uid = useUID();
  const enableCwhPreferencesApi = useFlag(FLAGS.ENABLE_CWH_PREFERENCES_API);
  const useCwh = enableCwhPreferencesApi && !isChina;
  const result = useQuery({
    queryKey: ["userPreferences", uid],
    queryFn: async () =>
      useCwh
        ? await fetchCwhUserPreferences()
        : await fetchUserPreferences(uid),
    enabled: !!uid,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  if (result.data?.["language"]) {
    localStorage.setItem("hej-language", result.data?.["language"] as string);
  }
  return result;
}

async function makeSetMultiplePreferences(
  uid: string,
  update: Partial<UserPreferences>
) {
  const data = await httpClient.core.put<{ preferences: UserPreferences }>(
    `/users/${uid}/preferences`,
    {
      body: { update },
    }
  );
  return data.preferences;
}

/**
 * Writes preferences directly to CWH. CWH distinguishes create from update:
 * we GET the current preferences and use PUT when every submitted key already exists, otherwise POST.
 */
async function upsertCwhPreferences(
  update: Record<string, unknown>
): Promise<Partial<UserPreferences>> {
  const body = { userPreferences: toCwhUserPreferences(update) };

  const existingKeys = new Set(
    toUserPreferenceList(await fetchRawCwhPreferences()).map((p) => p.key)
  );
  const submittedKeys = body.userPreferences.map((p) => p.key);
  const allExist =
    submittedKeys.length > 0 &&
    submittedKeys.every((key) => existingKeys.has(key));

  if (allExist) {
    await httpClient.cwh.put(CWH_PREFERENCES_PATH, { body });
  } else {
    await httpClient.cwh.post(CWH_PREFERENCES_PATH, { body });
  }

  return update;
}

function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  const uid = useUID();
  const enableCwhPreferencesApi = useFlag(FLAGS.ENABLE_CWH_PREFERENCES_API);
  const useCwh = enableCwhPreferencesApi && !isChina;

  const queryKey = ["userPreferences", uid];
  const mutation = useMutation<
    Partial<UserPreferences>,
    unknown,
    { [x: string]: any },
    { previousPreferences: UserPreferences }
  >({
    mutationKey: queryKey,
    mutationFn: (update) =>
      useCwh
        ? upsertCwhPreferences(update)
        : makeSetMultiplePreferences(uid, update),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey });
      const previousPreferences = queryClient.getQueryData(
        queryKey
      ) as UserPreferences;

      queryClient.setQueryData<UserPreferences>(queryKey, (old) => {
        return { ...old, ...update };
      });

      return { previousPreferences };
    },
    onSuccess: (data) => {
      if (useCwh) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }
      queryClient.setQueryData(queryKey, data["preferences"]);
    },
    onError: (_err, _update, ctx) =>
      queryClient.setQueryData(queryKey, ctx?.previousPreferences),
  });

  return mutation;
}

export function useUserPreference<SettingType>(
  settingName: string,
  fallback: SettingType
): [SettingType, (value: SettingType) => void, boolean] {
  const { data: preferences, isLoading } = useUserPreferences();
  const mutation = useUpdateUserPreferences();

  const setPreference = React.useCallback(
    (newValue: any) => {
      mutation.mutate({ [settingName]: newValue });
      if (settingName === userPreferences.LANGUAGE) {
        localStorage?.setItem("hej-language", newValue);
      }
    },
    [mutation, settingName]
  );

  return [
    (preferences?.[settingName] as SettingType) ?? fallback,
    setPreference,
    isLoading,
  ];
}
